import { useContext, useEffect } from 'react'
import type { Locale } from '@seaa/shared'
import { useI18n } from './i18n'
import { ServerPageMetadataContext } from './page-metadata-context'

type JsonLd = Record<string, unknown>

export interface PageMetadata {
  enabled?: boolean
  title: string
  description: string
  canonicalUrl?: string
  imageUrl?: string | null
  type?: 'website' | 'business.business'
  robots?: string
  alternates?: Array<{ locale: Locale; url: string }>
  structuredData?: JsonLd | JsonLd[]
  revision?: string
}

/** Keeps metadata route-scoped in the SPA and restores the previous page on unmount. */
export function usePageMetadata(metadata: PageMetadata) {
  const { locale } = useI18n()
  const serverMetadata = useContext(ServerPageMetadataContext)
  const localizedCanonical = metadata.alternates?.find((entry) => entry.locale === locale)?.url ?? metadata.canonicalUrl
  if (serverMetadata && metadata.enabled !== false) serverMetadata.current = { ...metadata, ...(localizedCanonical ? { canonicalUrl: localizedCanonical } : {}) }
  const structuredJson = JSON.stringify(metadata.structuredData ?? [])
  const alternatesJson = JSON.stringify(metadata.alternates ?? [])

  useEffect(() => {
    if (metadata.enabled === false) return
    const canonicalUrl = localizedCanonical ?? `${window.location.origin}${window.location.pathname}`
    const previousTitle = document.title
    const previousManaged = document.documentElement.dataset.titleManaged
    const cleanups: Array<() => void> = []
    document.documentElement.dataset.titleManaged = 'true'
    document.title = metadata.title

    const setHeadElement = (
      selector: string,
      tag: 'meta' | 'link',
      attributes: Record<string, string>,
    ) => {
      let node = document.head.querySelector<HTMLElement>(selector)
      const created = !node
      if (!node) {
        node = document.createElement(tag)
        document.head.append(node)
      }
      const previous = new Map(Object.keys(attributes).map((key) => [key, node!.getAttribute(key)]))
      for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, value)
      cleanups.push(() => {
        if (created) { node?.remove(); return }
        for (const [key, value] of previous) {
          if (value == null) node?.removeAttribute(key)
          else node?.setAttribute(key, value)
        }
      })
    }

    setHeadElement('meta[name="description"]', 'meta', { name: 'description', content: metadata.description })
    setHeadElement('meta[name="robots"]', 'meta', { name: 'robots', content: metadata.robots ?? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1' })
    setHeadElement('meta[property="og:title"]', 'meta', { property: 'og:title', content: metadata.title })
    setHeadElement('meta[property="og:description"]', 'meta', { property: 'og:description', content: metadata.description })
    setHeadElement('meta[property="og:type"]', 'meta', { property: 'og:type', content: metadata.type ?? 'website' })
    setHeadElement('meta[property="og:url"]', 'meta', { property: 'og:url', content: canonicalUrl })
    setHeadElement('meta[name="twitter:card"]', 'meta', { name: 'twitter:card', content: metadata.imageUrl ? 'summary_large_image' : 'summary' })
    setHeadElement('meta[name="twitter:title"]', 'meta', { name: 'twitter:title', content: metadata.title })
    setHeadElement('meta[name="twitter:description"]', 'meta', { name: 'twitter:description', content: metadata.description })
    setHeadElement('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonicalUrl })
    if (metadata.imageUrl) {
      setHeadElement('meta[property="og:image"]', 'meta', { property: 'og:image', content: metadata.imageUrl })
      setHeadElement('meta[name="twitter:image"]', 'meta', { name: 'twitter:image', content: metadata.imageUrl })
    }

    const alternates = JSON.parse(alternatesJson) as NonNullable<PageMetadata['alternates']>
    // Replace the response's route-specific metadata when hydrating or navigating.
    const serverNodes = [...document.head.querySelectorAll('[data-seaa-server-metadata]')]
    serverNodes.forEach((node) => node.remove())
    cleanups.push(() => serverNodes.forEach((node) => document.head.append(node)))
    for (const alternate of alternates) {
      const link = document.createElement('link')
      link.rel = 'alternate'
      link.hreflang = alternate.locale
      link.href = alternate.url
      link.dataset.seaaManaged = 'alternate'
      document.head.append(link)
      cleanups.push(() => link.remove())
    }
    if (alternates.length > 0) {
      const fallback = document.createElement('link')
      fallback.rel = 'alternate'
      fallback.hreflang = 'x-default'
      fallback.href = alternates.find((entry) => entry.locale === 'ar')?.url ?? canonicalUrl
      fallback.dataset.seaaManaged = 'alternate'
      document.head.append(fallback)
      cleanups.push(() => fallback.remove())
    }

    const structured = JSON.parse(structuredJson) as JsonLd | JsonLd[]
    const entries = Array.isArray(structured) ? structured : [structured]
    for (const entry of entries) {
      if (Object.keys(entry).length === 0) continue
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.dataset.seaaManaged = 'structured-data'
      script.text = JSON.stringify(entry)
      document.head.append(script)
      cleanups.push(() => script.remove())
    }

    return () => {
      for (const cleanup of cleanups.reverse()) cleanup()
      document.title = previousTitle
      if (previousManaged) document.documentElement.dataset.titleManaged = previousManaged
      else delete document.documentElement.dataset.titleManaged
    }
  }, [alternatesJson, localizedCanonical, metadata.description, metadata.enabled, metadata.imageUrl, metadata.revision, metadata.robots, metadata.title, metadata.type, structuredJson])
}
